import {
  capturePostHogEvent,
  identifyPostHogUser,
} from "../lib/analytics/posthog.client";

export function captureAnalyticsEvent(
  event: string,
  properties?: Record<string, any>,
) {
  if (typeof window === "undefined") return;

  try {
    capturePostHogEvent(event, properties);
  } catch {
    // Analytics must never block an explicit user action or page rendering.
  }
}

export function useAnalytics() {
  const trackEvent = captureAnalyticsEvent;

  const trackCodingProblemView = (
    problemId: string,
    title: string,
    difficulty: string,
  ) => {
    trackEvent("coding_problem_viewed", {
      problem_id: problemId,
      problem_title: title,
      difficulty,
    });
  };

  const trackCodingProblemStarted = (problemId: string, title: string) => {
    trackEvent("coding_problem_started", {
      problem_id: problemId,
      problem_title: title,
    });
  };

  const trackCodingProblemCompleted = (
    problemId: string,
    title: string,
    timeSpent?: number,
  ) => {
    trackEvent("coding_problem_completed", {
      problem_id: problemId,
      problem_title: title,
      time_spent_seconds: timeSpent,
    });
  };

  const trackCourseView = (courseId: string, title: string) => {
    trackEvent("course_viewed", {
      course_id: courseId,
      course_title: title,
    });
  };

  const trackLessonStarted = (
    lessonId: string,
    courseId: string,
    title: string,
  ) => {
    trackEvent("lesson_started", {
      lesson_id: lessonId,
      course_id: courseId,
      lesson_title: title,
    });
  };

  const trackUserSignup = (method?: string) => {
    trackEvent("user_signed_up", {
      signup_method: method,
    });
  };

  const trackUserLogin = (method?: string) => {
    trackEvent("user_logged_in", {
      login_method: method,
    });
  };

  // ========== 記事関連イベント ==========

  /**
   * 記事一覧ページ表示時
   */
  const trackArticleListView = (properties?: {
    filters?: Record<string, string>;
    sort?: string;
    referrer?: string;
  }) => {
    trackEvent("article_list_view", properties);
  };

  /**
   * 記事検索実行時
   */
  const trackArticleSearch = (query: string, resultsCount: number) => {
    trackEvent("article_search", {
      query,
      results_count: resultsCount,
    });
  };

  /**
   * フィルタ変更時
   */
  const trackArticleFilterChange = (
    filterName: string,
    filterValue: string,
    allFilters?: Record<string, string>,
  ) => {
    trackEvent("article_filter_change", {
      filter_name: filterName,
      filter_value: filterValue,
      all_filters: allFilters,
    });
  };

  /**
   * 並び替え変更時
   */
  const trackArticleSortChange = (sortOrder: string) => {
    trackEvent("article_sort_change", {
      sort_order: sortOrder,
    });
  };

  /**
   * 記事カードクリック時
   */
  const trackArticleCardClick = (
    articleId: string,
    articleTitle: string,
    properties?: {
      position?: number;
      block?: "pickup" | "main" | "newest" | "popular" | "related";
      category?: string;
      filters?: Record<string, string>;
      sort?: string;
    },
  ) => {
    trackEvent("article_card_click", {
      article_id: articleId,
      article_title: articleTitle,
      ...properties,
    });
  };

  /**
   * 記事詳細ページ表示時
   */
  const trackArticleDetailView = (
    articleId: string,
    articleTitle: string,
    properties?: {
      category?: string;
      readTime?: number;
      referrer?: string;
    },
  ) => {
    trackEvent("article_detail_view", {
      article_id: articleId,
      article_title: articleTitle,
      ...properties,
    });
  };

  /**
   * 関連記事クリック時
   */
  const trackArticleRelatedClick = (
    currentArticleId: string,
    relatedArticleId: string,
    relatedArticleTitle: string,
  ) => {
    trackEvent("article_related_click", {
      current_article_id: currentArticleId,
      related_article_id: relatedArticleId,
      related_article_title: relatedArticleTitle,
    });
  };

  /**
   * 記事からコースCTAクリック時
   */
  const trackArticleToCourseClick = (
    articleId: string,
    courseSlug: string,
    properties?: {
      position?: string;
    },
  ) => {
    trackEvent("article_to_course_click", {
      article_id: articleId,
      course_slug: courseSlug,
      ...properties,
    });
  };

  const identifyUser = (userId: string, properties?: Record<string, any>) => {
    if (typeof window !== "undefined") {
      identifyPostHogUser(userId, properties);
    }
  };

  return {
    trackEvent,
    trackCodingProblemView,
    trackCodingProblemStarted,
    trackCodingProblemCompleted,
    trackCourseView,
    trackLessonStarted,
    trackUserSignup,
    trackUserLogin,
    identifyUser,
    // 記事関連
    trackArticleListView,
    trackArticleSearch,
    trackArticleFilterChange,
    trackArticleSortChange,
    trackArticleCardClick,
    trackArticleDetailView,
    trackArticleRelatedClick,
    trackArticleToCourseClick,
  };
}
